import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Package, Server, Heart, Factory, FileText, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ICON_MAP = { Package, Server, Heart, Factory };

/**
 * ESATemplatePicker — modal that shows industry preset templates + "Start from scratch".
 * Picking a preset POSTs /presets/{id}/create then navigates to the edit form.
 */
const ESATemplatePicker = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);

  useEffect(() => {
    if (!open) return;
    const fetchPresets = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/electrical-safety-audit/presets/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load templates');
        const data = await res.json();
        setPresets(data.presets || []);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPresets();
  }, [open]);

  const handlePickPreset = async (presetId) => {
    setCreating(presetId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/electrical-safety-audit/presets/${presetId}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to create from template');
      const report = await res.json();
      toast.success(`Created ${report.report_no} from ${presets.find(p => p.id === presetId)?.label} template`);
      onClose?.();
      navigate(`/projects/project-reports/audit/electrical-safety/${report.id}/edit`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(null);
    }
  };

  const handleBlank = () => {
    onClose?.();
    navigate('/projects/project-reports/audit/electrical-safety/new');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="esa-template-picker"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Choose an Audit Template</h2>
              <p className="text-xs text-slate-500">Pre-populate scope, assets, and sample findings based on facility type.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="mx-auto animate-spin text-blue-500" size={32} />
              <p className="text-sm text-slate-500 mt-3">Loading templates…</p>
            </div>
          ) : (
            <>
              {/* Start from scratch */}
              <button
                onClick={handleBlank}
                data-testid="template-blank"
                className="w-full p-4 border-2 border-dashed border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/50 transition-colors flex items-center gap-4 text-left"
              >
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="text-slate-600" size={22} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-800">Start from Scratch</p>
                  <p className="text-xs text-slate-500">Blank report — fill in everything yourself</p>
                </div>
                <span className="text-xs text-slate-400">→</span>
              </button>

              <div className="flex items-center gap-2 my-2">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">OR PICK AN INDUSTRY PRESET</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Preset grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {presets.map((p) => {
                  const Icon = ICON_MAP[p.icon] || FileText;
                  const isCreating = creating === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => !creating && handlePickPreset(p.id)}
                      disabled={!!creating}
                      data-testid={`template-${p.id}`}
                      className={`p-4 border rounded-xl text-left transition-all ${
                        isCreating
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-300 hover:shadow-md disabled:opacity-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          {isCreating ? <Loader2 className="text-white animate-spin" size={22} /> : <Icon className="text-white" size={22} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 mb-1">{p.label}</p>
                          <p className="text-xs text-slate-500 line-clamp-2 mb-2">{p.description}</p>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded-full border border-blue-100">
                              {p.activities_count} activities
                            </span>
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-semibold rounded-full border border-emerald-100">
                              {p.assets_count} assets
                            </span>
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-semibold rounded-full border border-amber-100">
                              {p.findings_count} sample findings
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <p className="text-[11px] text-slate-500">
            Templates pre-populate scope, deliverables, default assets, sample findings, and applicable code references. You can still edit everything afterwards.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ESATemplatePicker;
